import { useCallback, useMemo } from 'react'

import { RoleType } from '@tribeplatform/gql-client/types'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { Chart } from '../components/Chart.js'
import type { ChartProps } from '../types/index.js'
import { AnalyticsFeeder } from '../utils/analyticsFeeder.js'
import type { AnalyticsQuery } from '../utils/generateAnalyticsQuery.js'
import {
  generateAnalyticsQuery,
  generateTimeFrame,
  generateGroupBy,
} from '../utils/generateAnalyticsQuery.js'
import { SecondaryChartColors } from '../utils/index.js'
import { getValueFromAnalyticsData } from '../utils/parser.js'

const roleCount = Object.keys(RoleType).length

export const PageViewsArea = ({
  scope,
  id,
  timeFrame,
  className,
  segment,
}: ChartProps) => {
  const { $t } = useI18n()

  const createQueries = useCallback(() => {
    const generalQuery: AnalyticsQuery = {
      selects: [],
      timeFrame: generateTimeFrame({ timeFrame }),
      where: [
        { field: scope, value: id },
        { field: 'space_type', operator: '!=', value: 'PRIVATE_MESSAGE' },
        ...(segment?.where ? [segment.where] : []),
      ],
      limit: (timeFrame.xAxisCount + 2) * roleCount,
    }

    const currentQuery: AnalyticsQuery = {
      ...generalQuery,
      selects: [{ field: 'count(view)', as: 'Views' }],
    }

    const prevQuery: AnalyticsQuery = {
      ...currentQuery,
      timeFrame: generateTimeFrame({ timeFrame, target: 'previous' }),
    }

    const chartQuery: AnalyticsQuery = {
      ...currentQuery,
      groupBy: {
        ...generateGroupBy(timeFrame.granularity),
      },
      order: { by: 'timeBucket', dir: 'asc' },
    }

    if (timeFrame.id === 'allTime') {
      return [
        generateAnalyticsQuery(chartQuery),
        generateAnalyticsQuery(currentQuery),
      ]
    }

    return [
      generateAnalyticsQuery(chartQuery),
      generateAnalyticsQuery(currentQuery),
      generateAnalyticsQuery(prevQuery),
    ]
  }, [id, scope, timeFrame, segment])

  const queries = useMemo(() => [createQueries()], [createQueries])

  return (
    <AnalyticsFeeder queries={queries[0]}>
      {({ data, isLoading }) => (
        <Chart
          type="Area"
          title={$t(
            {
              id: 'Analytics.PageViewsChartTitle',
              defaultMessage: 'Page views ({granularity})',
            },
            {
              granularity: timeFrame.granularityText,
            },
          )}
          description={timeFrame.text}
          current={getValueFromAnalyticsData(data[1], 'Views')}
          previous={getValueFromAnalyticsData(data[2], 'Views')}
          data={data[0]}
          syncId="analytics"
          timeFrame={timeFrame}
          indexBy={['Views']}
          labels={[
            $t({
              id: 'Generics.Views',
              defaultMessage: 'Views',
            }),
          ]}
          isLoading={isLoading}
          className={className}
          ColorTheme={SecondaryChartColors}
        />
      )}
    </AnalyticsFeeder>
  )
}
